import type { FrameProps } from "@/components/shapes/shared/frame-props";
import { useT } from "@/i18n";
import { framesFileMessages } from "@/i18n/messages/frames-file";
import { cardPaint } from "../shared/card-paint";
import { FrameEmptyState } from "../shared/frame-empty-state";
import { ObjectFrame } from "../shared/object-frame";
import { hasFile } from "./file-card-shape";
import { FileFace } from "./file-face";
import { FileActionButtons, FileNoticeText } from "./file-notice";
import { useFileNote, type FileActions } from "./use-file-actions";

/**
 * A file shown as itself rather than as its document: its card, larger,
 * with what it offers here, or where it is when it is not here.
 */
export function FileObject({
  id,
  data,
  actions,
}: Pick<FrameProps, "id" | "data"> & { actions: FileActions }) {
  const t = useT(framesFileMessages);
  const content = data.content ?? {};
  const name = typeof content.name === "string" ? content.name : "";
  const note = useFileNote(content);
  return (
    <ObjectFrame id={id} paint={cardPaint(data)} testId="file-frame">
      {!hasFile(content) ? (
        <FrameEmptyState
          frameId={id}
          status={data.status}
          testId="file-frame-empty"
        >
          {t("noFile")}
        </FrameEmptyState>
      ) : (
        <>
          <FileFace
            source={content}
            density="regular"
            note={note}
            testIdPrefix="file-frame-face"
          />
          {actions.notice && (
            <FileNoticeText
              notice={actions.notice}
              className="shrink-0 px-[1.4em] pb-[0.6em] text-[0.62em]"
            />
          )}
          <FileActionButtons
            actions={actions}
            name={name}
            scale="object"
            className="shrink-0 px-[1.4em]"
          />
        </>
      )}
    </ObjectFrame>
  );
}
